// src/components/reservation/PrintBooking.js
import React, { useState } from 'react';
import { 
    Box, 
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import { 
    Print as PrintIcon,
    Warning as WarningIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { formatNumberWithComma, formatCreatedAtDate, getCategoriesNameById } from '../../utils/helpers';
import { companyInfo } from '../../utils/dataInfo';
import { paymentModes } from '../../utils/dataInfo';

const PrintBooking = ({ bookingData, onPrintComplete, rooms = [] }) => {
    const [printError, setPrintError] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);

    const generateReceiptHTML = () => {
        const booking = bookingData;
        const isIndividual = booking.booking_type === 1;
        
        // Format dates
        const createdDate = formatCreatedAtDate(booking.created_at);
        const createdTime = booking.created_at ? new Date(booking.created_at).toLocaleTimeString() : '';
        const checkInDate = booking.check_in ? new Date(booking.check_in).toLocaleDateString() : 'N/A';
        const checkOutDate = booking.check_out ? new Date(booking.check_out).toLocaleDateString() : 'N/A';
        
        // Get payment mode name
        const paymentModeName = paymentModes.find(mode => mode.id === booking.payment_mode)?.name || booking.payment_mode || 'N/A';
        
        // Calculate balance due
        const balanceDue = (booking.total_amount || 0) - (booking.advance_amount || 0);
        
        // Get additional charges
        let additionalCharges = {};
        try {
            additionalCharges = typeof booking.additional_charges === 'string' 
                ? JSON.parse(booking.additional_charges || '{}') 
                : (booking.additional_charges || {});
        } catch (e) {
            additionalCharges = {};
        }

        return `
        <!DOCTYPE html>
        <html>
            <head>
                <title>Booking Details - ${booking.booking_no}</title>
                <style>
                    @page { 
                        size: auto; 
                        margin: 10mm;
                    }
                    body { 
                        font-family: 'Arial', sans-serif; 
                        max-width: 210mm;
                        margin: 0 auto; 
                        padding: 20px;
                        font-size: 12px;
                        background: white;
                    }
                    .header { 
                        text-align: center; 
                        margin-bottom: 20px;
                        border-bottom: 2px solid #000;
                        padding-bottom: 10px;
                    }
                    .company-name { 
                        font-size: 24px; 
                        font-weight: bold; 
                        margin-bottom: 5px;
                        color: #2c3e50;
                    }
                    .company-info { 
                        font-size: 11px; 
                        margin: 2px 0;
                        color: #7f8c8d;
                    }
                    .title { 
                        text-align: center; 
                        font-size: 18px; 
                        font-weight: bold; 
                        margin: 20px 0;
                        background: #3498db;
                        color: white;
                        padding: 10px;
                        border-radius: 5px;
                    }
                    .section {
                        margin-bottom: 20px;
                        border: 1px solid #ddd;
                        border-radius: 5px;
                        overflow: hidden;
                    }
                    .section-title {
                        background: #34495e;
                        color: white;
                        padding: 10px 15px;
                        font-weight: bold;
                        font-size: 14px;
                        margin: 0;
                    }
                    .section-content {
                        padding: 15px;
                    }
                    .info-row { 
                        display: flex; 
                        justify-content: space-between; 
                        margin: 8px 0; 
                        font-size: 12px;
                        border-bottom: 1px dotted #eee;
                        padding-bottom: 5px;
                    }
                    .info-grid { 
                        display: grid; 
                        grid-template-columns: 1fr 1fr; 
                        gap: 15px; 
                    }
                    .info-label { 
                        color: #7f8c8d; 
                        font-weight: normal;
                        min-width: 150px;
                    }
                    .info-value { 
                        font-weight: bold;
                        color: #2c3e50;
                    }
                    .status-badge {
                        display: inline-block;
                        padding: 3px 10px;
                        border-radius: 3px;
                        font-size: 11px;
                        font-weight: bold;
                    }
                    .status-active { background: #3498db; color: white; }
                    .status-checkedin { background: #27ae60; color: white; }
                    .status-checkedout { background: #95a5a6; color: white; }
                    .status-cancelled { background: #e74c3c; color: white; }
                    .items-table { 
                        width: 100%; 
                        border-collapse: collapse; 
                        margin: 10px 0; 
                    }
                    .items-table th { 
                        text-align: left; 
                        border-bottom: 2px solid #ddd; 
                        padding: 10px;
                        background: #ecf0f1;
                        font-size: 11px;
                    }
                    .items-table td { 
                        padding: 8px 10px; 
                        border-bottom: 1px solid #eee;
                    }
                    .text-right { text-align: right; }
                    .text-center { text-align: center; }
                    .total-row { 
                        font-weight: bold; 
                        margin-top: 15px;
                        display: flex;
                        justify-content: space-between;
                        font-size: 14px;
                        border-top: 2px solid #000;
                        padding-top: 10px;
                    }
                    .grand-total {
                        font-weight: bold;
                        font-size: 16px;
                        background: #3498db;
                        color: white;
                        padding: 10px;
                        margin-top: 15px;
                        display: flex;
                        justify-content: space-between;
                        border-radius: 5px;
                    }
                    .footer { 
                        text-align: center; 
                        margin-top: 30px; 
                        font-size: 10px;
                        border-top: 1px solid #ddd;
                        padding-top: 15px;
                        color: #7f8c8d;
                    }
                    .watermark {
                        text-align: center;
                        font-size: 9px;
                        margin-top: 10px;
                        color: #95a5a6;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="company-name">${companyInfo.name}</div>
                    <div class="company-info">${companyInfo.address}</div>
                    <div class="company-info">Tel: ${companyInfo.phone} | Email: ${companyInfo.email || ''}</div>
                </div>
                
                <div class="title">BOOKING DETAILS</div>
                
                <div class="section">
                    <div class="section-title">Booking Information</div>
                    <div class="section-content">
                        <div class="info-grid">
                            <div>
                                <div class="info-row">
                                    <span class="info-label">Booking Number:</span>
                                    <span class="info-value">${booking.booking_no}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Booking Date:</span>
                                    <span class="info-value">${createdDate} at ${createdTime}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Booking Type:</span>
                                    <span class="info-value">${isIndividual ? 'Individual' : 'Organisation'}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Purpose of Visit:</span>
                                    <span class="info-value">${booking.purpose_of_visit || 'N/A'}</span>
                                </div>
                            </div>
                            <div>
                                <div class="info-row">
                                    <span class="info-label">Status:</span>
                                    <span class="info-value">
                                        <span class="status-badge status-${booking.status === 1 ? 'active' : booking.status === 2 ? 'checkedin' : booking.status === 3 ? 'checkedout' : 'cancelled'}">
                                            ${booking.status === 1 ? 'Active' : booking.status === 2 ? 'Checked In' : booking.status === 3 ? 'Checked Out' : 'Cancelled'}
                                        </span>
                                    </span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Check In:</span>
                                    <span class="info-value">${checkInDate}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Check Out:</span>
                                    <span class="info-value">${checkOutDate}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Arrival From:</span>
                                    <span class="info-value">${booking.arrival_from || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Room & Guest Information</div>
                    <div class="section-content">
                        <div class="info-grid">
                            <div>
                                <div class="info-row">
                                    <span class="info-label">Room Type:</span>
                                    <span class="info-value">${getCategoriesNameById(booking.room_type, rooms) || 'N/A'}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Adults:</span>
                                    <span class="info-value">${booking.adults || 1}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Children:</span>
                                    <span class="info-value">${booking.children || 0}</span>
                                </div>
                            </div>
                            <div>
                                <div class="info-row">
                                    <span class="info-label">Total Guests:</span>
                                    <span class="info-value">${(booking.adults || 1) + (booking.children || 0)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">${isIndividual ? 'Customer Information' : 'Organisation Information'}</div>
                    <div class="section-content">
                        ${isIndividual ? `
                            <div class="info-grid">
                                <div>
                                    <div class="info-row">
                                        <span class="info-label">Customer Name:</span>
                                        <span class="info-value">${booking.customer_name || 'N/A'}</span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">Mobile Number:</span>
                                        <span class="info-value">${booking.customer_mobile || 'N/A'}</span>
                                    </div>
                                </div>
                                <div>
                                    <div class="info-row">
                                        <span class="info-label">Email:</span>
                                        <span class="info-value">${booking.customer_email || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        ` : `
                            <div>
                                <div class="info-row">
                                    <span class="info-label">Organisation Name:</span>
                                    <span class="info-value">${booking.organisation_name || 'N/A'}</span>
                                </div>
                                ${booking.organisation_members && booking.organisation_members.length > 0 ? `
                                    <div style="margin-top: 15px;">
                                        <strong>Members (${booking.organisation_members.length}):</strong>
                                        <table class="items-table">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Phone</th>
                                                    <th>Email</th>
                                                    <th>Room</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                ${booking.organisation_members.map(member => `
                                                    <tr>
                                                        <td>${member.name}</td>
                                                        <td>${member.phone}</td>
                                                        <td>${member.email || '-'}</td>
                                                        <td>${getCategoriesNameById(member.room_type, rooms) || 'N/A'}</td>
                                                    </tr>
                                                `).join('')}
                                            </tbody>
                                        </table>
                                    </div>
                                ` : ''}
                            </div>
                        `}
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Payment Details</div>
                    <div class="section-content">
                        <div class="info-grid">
                            <div>
                                <div class="info-row">
                                    <span class="info-label">Payment Mode:</span>
                                    <span class="info-value">${paymentModeName}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Booking Charge:</span>
                                    <span class="info-value">${formatNumberWithComma(booking.booking_charge || 0)}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Discount:</span>
                                    <span class="info-value">- ${formatNumberWithComma(booking.discount_amount || 0)}</span>
                                </div>
                            </div>
                            <div>
                                <div class="info-row">
                                    <span class="info-label">Tax Charge:</span>
                                    <span class="info-value">${formatNumberWithComma(booking.tax_charge || 0)}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Service Charge:</span>
                                    <span class="info-value">${formatNumberWithComma(booking.service_charge || 0)}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Total Charge:</span>
                                    <span class="info-value">${formatNumberWithComma(booking.total_charge || 0)}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="total-row">
                            <span>Total Amount:</span>
                            <span>${formatNumberWithComma(booking.total_amount || 0)}</span>
                        </div>
                        
                        <div class="info-row">
                            <span class="info-label">Advance Paid:</span>
                            <span class="info-value">${formatNumberWithComma(booking.advance_amount || 0)}</span>
                        </div>
                        
                        <div class="grand-total">
                            <span>Balance Due:</span>
                            <span>${formatNumberWithComma(balanceDue)}</span>
                        </div>
                        
                        ${booking.advance_remarks ? `
                            <div class="info-row">
                                <span class="info-label">Advance Remarks:</span>
                                <span class="info-value">${booking.advance_remarks}</span>
                            </div>
                        ` : ''}
                        
                        ${booking.discount_reason ? `
                            <div class="info-row">
                                <span class="info-label">Discount Reason:</span>
                                <span class="info-value">${booking.discount_reason}</span>
                            </div>
                        ` : ''}
                    </div>
                </div>

                ${booking.checkout_remarks ? `
                    <div class="section">
                        <div class="section-title">Checkout Information</div>
                        <div class="section-content">
                            <div class="info-row">
                                <span class="info-label">Checkout Remarks:</span>
                                <span class="info-value">${booking.checkout_remarks}</span>
                            </div>
                            ${booking.checked_out_by ? `
                                <div class="info-row">
                                    <span class="info-label">Checked Out By:</span>
                                    <span class="info-value">${booking.checked_out_by}</span>
                                </div>
                            ` : ''}
                            ${booking.actual_check_out ? `
                                <div class="info-row">
                                    <span class="info-label">Actual Checkout Date:</span>
                                    <span class="info-value">${new Date(booking.actual_check_out).toLocaleDateString()}</span>
                                </div>
                            ` : ''}
                        </div>
                    </div>
                ` : ''}

                ${booking.booking_remarks ? `
                    <div class="section">
                        <div class="section-title">Remarks</div>
                        <div class="section-content">
                            <div class="info-row">
                                <span class="info-value">${booking.booking_remarks}</span>
                            </div>
                        </div>
                    </div>
                ` : ''}

                <div class="footer">
                    <div>${companyInfo.receiptFooter || 'Thank you for choosing us!'}</div>
                    <div class="watermark">This is a computer generated document</div>
                </div>
            </body>
        </html>
        `;
    };

    const handlePrint = () => {
        setIsPrinting(true);
        setPrintError(false);

        try {
            const printContent = generateReceiptHTML();
            
            const printWindow = window.open('', '_blank');
            
            if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
                setPrintError(true);
                setIsPrinting(false);
                return;
            }

            printWindow.document.write(printContent);
            printWindow.document.close();
            
            printWindow.focus();
            
            setTimeout(() => {
                try {
                    printWindow.print();
                    
                    printWindow.onafterprint = () => {
                        printWindow.close();
                        setIsPrinting(false);
                        if (onPrintComplete) onPrintComplete();
                    };
                    
                    setTimeout(() => {
                        if (!printWindow.closed) {
                            printWindow.close();
                        }
                        setIsPrinting(false);
                        if (onPrintComplete) onPrintComplete();
                    }, 1000);
                    
                } catch (printErr) {
                    console.error('Print error:', printErr);
                    setPrintError(true);
                    setIsPrinting(false);
                }
            }, 500);
            
        } catch (error) {
            console.error('Print error:', error);
            setPrintError(true);
            setIsPrinting(false);
        }
    };

    const handleManualPrint = () => {
        handlePrint();
    };

    React.useEffect(() => {
        const timer = setTimeout(() => {
            handlePrint();
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    if (printError) {
        return (
            <Dialog open={printError} onClose={() => setPrintError(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    <span>Print Error</span>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Pop-up blocker prevented the document from printing automatically.
                    </Alert>
                    <Typography variant="body2" paragraph>
                        Please click the button below to print manually, or check your browser settings to allow pop-ups for this site.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PrintIcon />}
                            onClick={handleManualPrint}
                            size="large"
                        >
                            Print Manually
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setPrintError(false);
                            if (onPrintComplete) onPrintComplete();
                        }}
                        color="error"
                        startIcon={<CloseIcon />}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    if (isPrinting) {
        return (
            <Dialog open={isPrinting}>
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={48} sx={{ mb: 2 }} />
                    <Typography>Preparing document for printing...</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Box sx={{ display: 'none' }}>
            {/* This component doesn't render anything visible when not printing */}
        </Box>
    );
};

export default PrintBooking;